import { useQueryClient } from 'react-query'

import { getBranding, saveBranding, saveBrandingLogo, deleteBranding } from 'features/branding/api/branding'
import { defaultBranding } from 'features/common/ui/theme/cartoTheme'
import { Branding } from '../models/Branding'

const brandingKeys = {
  all: ['branding'] as const
}

function useBrandingQueries() {
  const queryClient = useQueryClient()

  const queries = {
    getBranding: {
      queryKey: brandingKeys.all,
      queryFn: getBranding,
      retry: (failureCount, error) => {
        if (error && error.status === 404) {
          return false // do not retry if no custom branding
        }
        if (failureCount > 3) {
          return false
        }
        return true
      },
      staleTime: Infinity
    },
    saveBranding: {
      mutationFn: saveBranding,
      onSuccess: (data) => {
        queryClient.setQueryData(brandingKeys.all, (oldData?: Branding) => ({ ...data, logoUrl: oldData?.logoUrl }))
      }
    },
    saveBrandingOptimistic: {
      mutationFn: saveBranding,
      onMutate: async (data: Branding) => {
        // cancel ongoing queries so they don't overwrite our optimistic update
        await queryClient.cancelQueries(brandingKeys.all)
        const previous = queryClient.getQueryData(brandingKeys.all)

        // update to the new value before doing the api call
        queryClient.setQueryData(brandingKeys.all, (oldData?: Branding) => ({ ...data, logoUrl: oldData?.logoUrl }))

        // this will be received as "context" in onError
        return previous
      },
      onError: (error, data, context) => {
        // rollback query data to previous state if an error happens
        queryClient.setQueryData(brandingKeys.all, context)
      }
    },
    saveBrandingLogo: {
      mutationFn: saveBrandingLogo,
      onSuccess: (_, data) => {
        queryClient.setQueryData(brandingKeys.all, (oldData?: Branding) => ({
          ...(oldData as Branding),
          logoUrl: data.base64image
        }))
      }
    },
    deleteBranding: {
      mutationFn: deleteBranding,
      onSuccess: () => {
        queryClient.setQueryData(brandingKeys.all, defaultBranding)
      }
    }
  }

  return { queries }
}

export default useBrandingQueries
